import React, { useEffect, useRef, useState } from 'react'

import { ChevronDown } from "lucide-react";

import Metrics from '../../components/adminPage/analytics/Metrics';
import PageViewsChart from '../../components/adminPage/analytics/PageViewsChart';
import TrafficSourcesChart from '../../components/adminPage/analytics/TrafficSourcesChart';
import PageHero from '../../components/adminPage/PageHero';
import BounceRateTrend from '../../components/adminPage/analytics/BounceRateTrend';
import PopularUserLocations from '../../components/adminPage/analytics/PopularUserLocations';
import MostVisitedPages from '../../components/adminPage/analytics/MostVisitedPages';
import DomainList from '../../components/adminPage/analytics/DomainList';
import { useHttpClient } from '../../hooks/http-hook';

const options = [
    { value: "7d", label: "Son 7 gün" },
    { value: "30d", label: "Son 30 gün" },
    { value: "90d", label: "Son 90 gün" },
];

const AnalyticsDashboard = () => {
    const { error, isLoading, sendRequest } = useHttpClient();
    const [timeRange, setTimeRange] = useState({ value: "7d", label: "Son 7 gün" });
    const [viewType, setViewType] = useState("daily");
    const [isOpen, setIsOpen] = useState(false)
    const [selected, setSelected] = useState(options[0]);
    const [userLocations, setUserLocations] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [pageViews, setPageViews] = useState([]);
    const [trafficSourcesData, setTrafficSourcesData] = useState([]);
    const [mostVisitedPages, setMostVisitedPages] = useState([]);
    const [domains, setDomains] = useState([]);
    const [loading, setIsLoading] = useState(false)
    const ref = useRef(null);
    // Dinamik veri alma
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const selectedRange = timeRange?.value || viewType || "7d";
                const baseUrl = process.env.REACT_APP_BACKEND_URL;

                // İstekleri paralel başlat (credentials: 'include' eklendi)
                const [
                    chartRes,
                    summaryRes,
                    trafficRes,
                    locationsRes,
                    topPagesRes,
                    domainsRes,
                ] = await Promise.all([
                    fetch(`${baseUrl}/analytics/analytics-chartdata?range=${selectedRange}`, {
                        credentials: 'include',
                    }),
                    fetch(`${baseUrl}/analytics/analytics-summary?range=${selectedRange}`, {
                        credentials: 'include',
                    }),
                    fetch(`${baseUrl}/analytics/traffic-sources?range=${selectedRange}`, {
                        credentials: 'include',
                    }),
                    fetch(`${baseUrl}/analytics/analytics-locations?range=${selectedRange}`, {
                        credentials: 'include',
                    }),
                    fetch(`${baseUrl}/analytics/analytics-top-pages?range=${selectedRange}`, {
                        credentials: 'include',
                    }),
                    fetch(`${baseUrl}/analytics/analytics-domains?range=${selectedRange}`, { // düzeltildi
                        credentials: 'include',
                    }),
                ]);
               

                // JSON parse işlemleri
                const [
                    chartData,
                    summaryData,
                    trafficData,
                    locationsData,
                    topPagesData,
                    domainsData,
                ] = await Promise.all([
                    chartRes.json(),
                    summaryRes.json(),
                    trafficRes.json(),
                    locationsRes.json(),
                    topPagesRes.json(),
                    domainsRes.json(),
                ]);

                // State güncelle
                setPageViews(chartData);
                setMetrics(summaryData);
                setTrafficSourcesData(trafficData);
                setUserLocations(locationsData);
                setMostVisitedPages(topPagesData);
                setDomains(domainsData);

            } catch (error) {
                console.error('Veri çekilirken hata oluştu:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [timeRange, viewType]);

    // Dropdown dışına tıklanınca kapanması için
    useEffect(() => {
        function handleClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, []);

    return (
        <div className="min-h-screen bg-background pb-20">
            {/* Hero Section */}
            <PageHero
                pageTitle="Google Analizleri"
                pageSubtitle="Verilerinizi eyleme dönüştürülebilir içgörülere dönüştürün"
                children={
                    <>
                        <div className="relative w-36" ref={ref}>
                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                className="w-full h-10 flex items-center justify-between px-3 py-2 bg-white/10 text-white border border-white/20 rounded-md text-sm hover:bg-white/20 transition"
                            >
                                {selected.label}
                                <ChevronDown className="w-4 h-4 opacity-70" />
                            </button>

                            {isOpen && (
                                <ul className="absolute z-50 mt-2 w-full bg-zinc-100 overflow-hidden rounded-md shadow-lg text-sm text-zinc-800">
                                    {options.map((option) => (
                                        <li
                                            key={option.value}
                                            onClick={() => {
                                                setTimeRange(option)
                                                setSelected(option)
                                                setIsOpen(false)
                                            }}
                                            className={`px-3 m-0 py-2 cursor-pointer hover:bg-emerald-200/80 ${selected.value === option.value ? "bg-emerald-100" : ""
                                                }`}
                                        >
                                            {option.label}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="flex bg-white/10 rounded-lg p-1">
                            {[
                                { key: "daily", label: "Günlük" },
                                { key: "weekly", label: "Haftalık" },
                                { key: "monthly", label: "Aylık" }
                            ].map((type) => (
                                <button
                                    key={type.key}
                                    variant={viewType === type.key ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => {
                                        setTimeRange("")
                                        setViewType(type.key)
                                    }}
                                    className={`h-9 rounded-md px-3 text-primaryForeground ${viewType === type.key ? "bg-white text-primary" : "text-white hover:bg-white/20"} `}
                                >
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    </>} />

            {/* Main Content */}

            <div className="max-w-7xl mx-auto px-6 py-8">
                <>
                    {/* Key Metrics */}
                    <Metrics metrics={metrics && metrics} />

                    {/* Charts Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

                        {/* Page Views Chart */}
                        <PageViewsChart pageViews={pageViews && pageViews} />

                        {/* Traffic Sources */}
                        <TrafficSourcesChart trafficSourcesData={trafficSourcesData} />
                    </div>

                    {/* Bottom Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Bounce Rate Trend */}
                        <BounceRateTrend bounceRateData={pageViews && pageViews} />
                        {/* User Locations */}
                        <PopularUserLocations userLocations={userLocations && userLocations} />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                        <MostVisitedPages mostVisitedPages={mostVisitedPages && mostVisitedPages} />
                        <DomainList domains={domains && domains} />
                    </div>

                </>
            </div>
        </div>
    )
}

export default AnalyticsDashboard

//veyis_karakus@hotmail.com
//Şifre : Veyis13259.